import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [input, setInput] = useState('');
  const [result, setResult] = useState('');
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  // PWA Install functionality
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    const handleAppInstalled = () => {
      setShowInstallButton(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowInstallButton(false);
      }
      setDeferredPrompt(null);
    }
  };

  // Enhanced Chinese number parsing with comprehensive Pinyin support
  const DIGITS = {
    "零": 0, "〇": 0, "0": 0, "ling": 0, "líng": 0,
    "一": 1, "壹": 1, "1": 1, "yi": 1, "yī": 1, "yí": 1, "yǐ": 1, "yì": 1,
    "二": 2, "两": 2, "贰": 2, "2": 2, "er": 2, "èr": 2, "liang": 2, "liǎng": 2,
    "三": 3, "叁": 3, "3": 3, "san": 3, "sān": 3,
    "四": 4, "肆": 4, "4": 4, "si": 4, "sì": 4,
    "五": 5, "伍": 5, "5": 5, "wu": 5, "wǔ": 5,
    "六": 6, "陆": 6, "6": 6, "liu": 6, "liù": 6,
    "七": 7, "柒": 7, "7": 7, "qi": 7, "qī": 7,
    "八": 8, "捌": 8, "8": 8, "ba": 8, "bā": 8,
    "九": 9, "玖": 9, "9": 9, "jiu": 9, "jiǔ": 9,
    ".": -1, "点": -1, "dian": -1, "diǎn": -1,
  };

  const SMALL_UNITS = {
    "十": 10, "拾": 10, "shi": 10, "shí": 10,
    "百": 100, "佰": 100, "bai": 100, "bǎi": 100,
    "千": 1000, "仟": 1000, "qian": 1000, "qiān": 1000,
  };

  const LARGE_UNITS = {
    "万": 1e4, "wan": 1e4, "wàn": 1e4,
    "億": 1e8, "亿": 1e8, "yì": 1e8, // Removed "yi" to avoid conflict with digit "yi"
    "兆": 1e12, "zhao": 1e12, "zhào": 1e12,
    "京": 1e16, "jing": 1e16, "jīng": 1e16,
  };

  const isDigitChar = (ch: string) => {
    return DIGITS.hasOwnProperty(ch) && (DIGITS as any)[ch] >= 0;
  };

  const isPointChar = (ch: string) => {
    return ch === "." || ch === "点" || ch === "dian" || ch === "diǎn";
  };

  const parseArabicLike = (s: string) => {
    const match = s.match(/^([+-]?\d+(?:\.\d+)?)([万億亿兆京]?)$/);
    if (!match) return null;
    const num = Number(match[1]);
    if (!isFinite(num)) return null;
    const unit = match[2] || "";
    const mul = unit ? ((LARGE_UNITS as any)[unit] || 1) : 1;
    return num * mul;
  };

  const parseChineseNumber = (rawInput: string) => {
    const input = rawInput.replace(/\s+/g, "").replace(/,/g, "");
    if (!input) return NaN;

    const arabic = parseArabicLike(input);
    if (arabic !== null) return arabic;

    // Check if input contains Pinyin and tokenize accordingly
    const hasPinyin = /(ling|líng|yi|yī|yí|yǐ|yì|er|èr|liang|liǎng|san|sān|si|sì|wu|wǔ|liu|liù|qi|qī|ba|bā|jiu|jiǔ|shi|shí|bai|bǎi|qian|qiān|wan|wàn|yì|zhao|zhào|jing|jīng|dian|diǎn)/i.test(input);
    
    if (hasPinyin) {
      // Handle Pinyin input - parse in sections separated by large units
      const tokens = rawInput.trim().split(/\s+/).filter(token => token.length > 0);
      let result = 0;
      let currentSection: string[] = [];

      const parseSection = (section: string[]) => {
        let sectionValue = 0;
        let current = 0;

        for (const token of section) {
          if (isDigitChar(token)) {
            current = current * 10 + (DIGITS as any)[token];
          } else if ((SMALL_UNITS as any)[token]) {
            if (current === 0) current = 1;
            sectionValue += current * (SMALL_UNITS as any)[token];
            current = 0;
          }
        }
        
        sectionValue += current;
        return sectionValue;
      };

      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        
        if ((LARGE_UNITS as any)[token]) {
          // Process current section and multiply by large unit
          let sectionValue = parseSection(currentSection);
          if (sectionValue === 0) sectionValue = 1;
          result += sectionValue * (LARGE_UNITS as any)[token];
          currentSection = [];
        } else {
          currentSection.push(token);
        }
      }
      
      // Process remaining section
      if (currentSection.length > 0) {
        result += parseSection(currentSection);
      }
      
      return result;
    } else {
      // Handle Chinese character input (original logic)
      const tokens = input.split("");

      const parseUnderWan = (chars: string[]) => {
        const maybeArabic = chars.join("");
        if (/^[+-]?\d+(?:\.\d+)?$/.test(maybeArabic)) {
          return Number(maybeArabic);
        }

        const pointIdx = chars.findIndex(ch => isPointChar(ch));
        const intChars = pointIdx >= 0 ? chars.slice(0, pointIdx) : chars;
        const decChars = pointIdx >= 0 ? chars.slice(pointIdx + 1) : [];

        let intVal = 0;
        let curr = 0;

        const emitUnit = (unit: number) => {
          if (curr === 0) curr = 1;
          intVal += curr * unit;
          curr = 0;
        };

        for (let i = 0; i < intChars.length; i++) {
          const ch = intChars[i];
          if (isDigitChar(ch)) {
            curr = curr * 10 + (DIGITS as any)[ch];
          } else if ((SMALL_UNITS as any)[ch]) {
            emitUnit((SMALL_UNITS as any)[ch]);
          }
        }
        intVal += curr;

        let decVal = 0;
        if (decChars.length) {
          let place = 0.1;
          for (const ch of decChars) {
            if (isDigitChar(ch)) {
              decVal += (DIGITS as any)[ch] * place;
              place /= 10;
            } else {
              break;
            }
          }
        }

        return intVal + decVal;
      };

      let total = 0;
      let section: string[] = [];

      const flush = (multiplier: number | null) => {
        const val = parseUnderWan(section);
        section = [];
        if (multiplier == null) {
          total += val;
        } else {
          total += val * multiplier;
        }
      };

      for (let i = 0; i < tokens.length; i++) {
        const ch = tokens[i];
        if ((LARGE_UNITS as any)[ch]) {
          flush((LARGE_UNITS as any)[ch]);
        } else {
          section.push(ch);
        }
      }
      flush(null);

      return total;
    }
  };

  const formatToChineseLarge = (n: number) => {
    const abs = Math.abs(n);
    const sign = n < 0 ? "-" : "";
    const fmt = (v: number) =>
      (Math.round(v * 100) / 100).toString().replace(/(\.\d*?[1-9])0+$|\.0+$/, "$1");

    if (abs >= 1e12) return sign + fmt(abs / 1e12) + "兆";
    if (abs >= 1e8) return sign + fmt(abs / 1e8) + "亿";
    if (abs >= 1e4) return sign + fmt(abs / 1e4) + "万";
    return sign + fmt(abs);
  };

  // Convert Chinese numbers to English formats
  const convertWanToEnglish = (value: string) => {
    const num = parseChineseNumber(value);
    if (isNaN(num)) return null;

    // Format with commas and show full number
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  // Convert English numbers to Chinese formats
  const convertEnglishToWan = (value: string) => {
    const num = parseFloat(value);
    if (isNaN(num)) return null;

    return formatToChineseLarge(num);
  };

  const handleConvert = () => {
    if (!input.trim()) {
      alert('Please enter a number');
      return;
    }

    // Check if input contains Chinese characters, Chinese number units, or Pinyin
    const hasChineseChars = /[\u4e00-\u9fff]/.test(input);
    const hasChineseUnits = /[万亿亿兆京]/.test(input);
    const hasPinyin = /(ling|líng|yi|yī|yí|yǐ|yì|er|èr|liang|liǎng|san|sān|si|sì|wu|wǔ|liu|liù|qi|qī|ba|bā|jiu|jiǔ|shi|shí|bai|bǎi|qian|qiān|wan|wàn|yì|zhao|zhào|jing|jīng|dian|diǎn)/i.test(input);
    
    if (hasChineseChars || hasChineseUnits || hasPinyin) {
      // Convert from Chinese to English
      const result = convertWanToEnglish(input);
      if (result === null) {
        alert('Please enter a valid Chinese number');
        return;
      }
      setResult(result);
    } else {
      // Convert from English to Chinese
      const num = parseFloat(input);
      if (isNaN(num)) {
        alert('Please enter a valid number');
        return;
      }
      const result = convertEnglishToWan(input);
      if (result === null) {
        alert('Please enter a valid number');
        return;
      }
      setResult(result);
    }
  };

  const clearAll = () => {
    setInput('');
    setResult('');
  };

  const examples = [
    { input: '一万五千', output: '15,000' },
    { input: 'yi wan wu qian', output: '15,000' },
    { input: 'yī wàn wǔ qiān', output: '15,000' },
    { input: '一百万', output: '1,000,000' },
    { input: '15000', output: '1.5万' },
  ];

  return (
    <div className="App">
      <div className="container">
        <div className="header">
          <h1 className="title">Number Converter</h1>
          <p className="subtitle">Convert between Chinese numbers, Pinyin, and English numbers</p>
          {showInstallButton && (
            <button className="install-button" onClick={handleInstallClick}>
              📱 Install App
            </button>
          )}
        </div>

        <div className="input-section">
          <label className="label">Enter a number:</label>
          <input
            className="input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="e.g., 一万五千 or yi wan wu qian or yī wàn wǔ qiān or 10000"
            onKeyPress={(e) => e.key === 'Enter' && handleConvert()}
          />
          
          <div className="button-container">
            <button className="convert-button" onClick={handleConvert}>
              Convert
            </button>
            <button className="clear-button" onClick={clearAll}>
              Clear
            </button>
          </div>
        </div>

        {result && (
          <div className="result-section">
            <div className="result-label">Result:</div>
            <div className="result-text">{result}</div>
          </div>
        )}

        <div className="examples-section">
          <h3 className="examples-title">Examples:</h3>
          {examples.map((example, index) => (
            <div key={index} className="example-item">
              <span className="example-input">{example.input}</span>
              <span className="example-arrow">→</span>
              <span className="example-output">{example.output}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default App;